import { toast } from "react-toastify";
import { ProfileIcon } from "../../../../components/cards/profilePic";
import { Icon } from "../../../../components/icon/icon";
import { CreatedEvent } from "../../../../redux/features/event/event.types";
import {
  copyTextToClipboard,
  getEventUrl,
  handleShare,
} from "../../../../utils/helpers";

const EYEBROW_LABEL_CLASS =
  "w-full font-onest text-[13px] font-medium uppercase leading-[18px] tracking-[0.08em] text-grey-600";

export const PreviewOrganizerDetails = ({
  event,
  className = "",
  canShare,
  showSocials = false,
}: {
  event?: CreatedEvent;
  className?: string;
  canShare: boolean;
  showSocials?: boolean;
}) => {
  const eventUrl = getEventUrl(event?.slug ?? "");
  const shareText = event?.name
    ? `Check out ${event.name} on Tikket!`
    : "Check out this event on Tikket!";

  const shareTargets: {
    key: string;
    icon: string;
    onClick: () => void;
    mobileOnly?: boolean;
  }[] = [
    {
      key: "whatsapp",
      icon: "whatsapp",
      onClick: () =>
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${shareText} ${eventUrl}`)}`,
          "_blank",
          "noreferrer",
        ),
    },
    {
      key: "x",
      icon: "twitter",
      onClick: () =>
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(eventUrl)}&text=${encodeURIComponent(shareText)}`,
          "_blank",
          "noreferrer",
        ),
    },
    {
      key: "tiktok",
      icon: "tiktok",
      mobileOnly: true,
      onClick: () => {
        handleShare("Tikket", shareText, eventUrl);
      },
    },
    {
      key: "instagram",
      icon: "instagram",
      mobileOnly: true,
      onClick: () => {
        handleShare("Tikket", shareText, eventUrl);
      },
    },
    {
      key: "snapchat",
      icon: "snapchat",
      onClick: () =>
        window.open(
          `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(eventUrl)}`,
          "_blank",
          "noreferrer",
        ),
    },
    {
      key: "link",
      icon: "link",
      onClick: () => {
        copyTextToClipboard(eventUrl).then(() => {
          toast.success("Link copied!");
        });
      },
    },
  ];

  const organizerSocialLinks = [
    {
      handle: "Email",
      url: `mailto:${event?.createdBy?.email}`,
      icon: "email-plain",
      visible: !!event?.createdBy?.email,
    },
    {
      handle: "Instagram",
      url: event?.createdBy?.instagramLink,
      icon: "instagram",
      visible: !!event?.createdBy?.instagramLink,
    },
    {
      handle: "X",
      url: event?.createdBy?.twitterLink,
      icon: "twitter",
      visible: !!event?.createdBy?.twitterLink,
    },
    {
      handle: "Website",
      url: event?.createdBy?.website,
      icon: "globe",
      visible: !!event?.createdBy?.website,
    },
  ];

  return (
    <div className={`flex flex-col gap-12 ${className}`}>
      <div className="flex w-full flex-col items-center justify-center gap-5">
        <p className={EYEBROW_LABEL_CLASS}>Organized by</p>

        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-center gap-[10px]">
            {event?.createdBy?.isOrganisation ? (
              <img
                alt="organization logo"
                src={event?.createdBy?.organisation?.logo}
                className={`h-8 min-h-8 w-8 min-w-8 max-w-8 overflow-hidden rounded-full object-cover ${!event?.createdBy?.organisation?.logo && "hidden"}`}
              />
            ) : (
              <ProfileIcon dimension={32} />
            )}

            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="font-onest text-base font-medium leading-6 text-primary underline decoration-[#c5d3e5]"
            >
              {event?.createdBy?.isOrganisation
                ? `${event?.createdBy?.organisation?.name}`
                : `${event?.createdBy?.firstName} ${event?.createdBy?.lastName}`}
            </a>

            {organizerSocialLinks.map((link) => {
              return link.visible ? (
                <a
                  key={link.handle}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon
                    svg={link.icon}
                    width={18}
                    height={18}
                    className="text-primary"
                  />
                </a>
              ) : undefined;
            })}
          </div>

          {!event?.createdBy?.isOrganisation ? (
            <>
              {event?.coOrganizers
                ?.filter(
                  (co) => co.role === "manager" && co.status === "accepted",
                )
                ?.map((co) => {
                  return (
                    <div
                      key={co.email}
                      className="flex w-full items-center gap-[10px]"
                    >
                      <ProfileIcon dimension={32} />

                      <p className="font-onest text-base font-medium leading-6 text-primary">
                        {`${co?.userId?.firstName} ${co?.userId?.lastName}`}
                      </p>
                    </div>
                  );
                })}
            </>
          ) : undefined}
        </div>
      </div>

      {showSocials ? (
        <div className="flex flex-col items-start gap-5">
          <p className={EYEBROW_LABEL_CLASS}>Share event</p>

          <div className="flex flex-wrap items-start gap-3">
            {shareTargets.map((target) => (
              <button
                key={target.key}
                type="button"
                onClick={target.onClick}
                disabled={!canShare}
                className={`flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-grey-200 transition-colors duration-200 hover:border-grey-300 hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-grey-200 disabled:hover:bg-transparent ${target.mobileOnly ? "md:hidden" : ""}`}
              >
                <Icon
                  svg={target.icon}
                  width={18}
                  height={18}
                  className="text-grey-900"
                />
              </button>
            ))}
          </div>
        </div>
      ) : undefined}
    </div>
  );
};
